/* ═══════════════════════════════════════════════════════════════════════════
   The contract, over the network
   ───────────────────────────────────────────────────────────────────────────
   One method per operation, one line of transport apiece, and a mapping layer
   between the server's model and this app's.

   This header used to say the opposite — *"there is no mapping layer because
   there is nothing to map: the types in `lib/types.ts` are the JSON on the
   wire, so a response is cast, not converted."* That claim was false when it
   was written and it is the reason the panel, which made the same one, spent a
   fortnight fixing white screens: a cast tells the compiler to stop checking,
   so every field the server did not send type-checked perfectly and arrived
   `undefined`. `money(undefined)` is `SARNaN`, and it rendered on the main
   browse surface.

   So there is a mapping layer, and as of now **its input side is generated**.
   The `Wire*` aliases below resolve to `wire/wire.gen.ts`, rendered from the Go
   structs by `make wire` in hexclean-api. What that buys is the thing a
   hand-written wire type cannot: rename a field in `internal/domain`,
   regenerate, and the conversion reading the old name stops compiling — here,
   in the one file whose subject is that the two models differ. Before this the
   shapes below were a careful guess, and a careful guess is still checked
   against nothing.

   The generated types stop at this file. A screen importing `Wire.Order` is a
   screen that has to know which of a field's two kinds of nothing it got —
   absent because `omitempty`, or `null` because Go marshals a nil slice that
   way — and ending that is the entire point.

   Deliberately thin otherwise. Every rule that could live here — which fields
   are required, how a promo is validated, who gets assigned a job — belongs to
   the server in this mode, and duplicating any of it client-side would produce
   two answers that disagree the first time the server changed. The mock
   implements those rules because it *is* the server; this file must not.

   `satisfies HexApi` at the bottom is what keeps the two in step: add a method
   to the contract and this file stops compiling until it exists here too.
   ═══════════════════════════════════════════════════════════════════════════ */

import { http } from './client'
import { routes } from './routes'
/* The server's side of every shape below, generated from the Go structs that
   decide it. Imported under one namespace so a reader can tell at a glance
   which side of the boundary a type belongs to: `Wire.Order` is what arrived,
   `Order` is what this app renders. */
import type * as Wire from '../wire/wire.gen'
import { identity } from '../identity'
import { lineKey } from '../lineKey'
import { initialsOf } from '../partner'
import { knownBadges } from '../badges'
import { HexApiError } from '../types'
import type {
  Area,
  City,
  ApplicationInput,
  FeaturedReview,
  HexApi,
  Menu,
  PlaceOrderInput,
  Quote,
  QuoteInput,
  SearchHit,
  SlotHold,
  SlotHoldInput,
} from './contract'
import type {
  Address,
  ListId,
  LocalizedText,
  Order,
  PartnerApplication,
  Promo,
  Provider,
  ProviderFilters,
  Review,
  ServiceCategory,
  SignInResult,
  TimeSlot,
  User,
} from '../types'

/** Public reads. The catalog is the same for everyone, so sending a bearer
 *  token with it would only stop a CDN or the service worker from caching it. */
const PUBLIC = { auth: false } as const

/** A cursor-paginated envelope, as the API sends every list that can grow.
 *
 *  Unwrapping it here is transport, not a rule: the screens that consume these
 *  render a list and have nowhere to put a cursor, and the ones that will
 *  eventually page — Explore, Orders — need a deliberate change rather than an
 *  accidental one. What this must never become is a place that *drops* results
 *  silently; `total` is carried past the boundary by nothing today, so a list
 *  longer than one page is a known limit, written here, not a surprise later. */
interface Page<T> {
  items: T[]
  total: number
  cursor: string | null
}

const items = <T>(page: Page<T>): T[] => page.items ?? []

/** A day of windows, as availability sends them.
 *
 *  The server groups by day because a picker that shows "Tuesday — closed" has
 *  to know a day exists with nothing in it. This app's picker reads the date
 *  off each window instead, so the grouping is flattened away at the boundary.
 *  Windows carry two fields beyond `TimeSlot` — `kind` and `remaining` — which
 *  nothing here reads yet; `remaining` is the better "2 left today" copy if the
 *  picker ever wants it. */
type WireSlotDay = Wire.SlotDay

/** A partner as the API sends them.
 *
 *  The widest gap in this file: the service publishes thirteen fields and this
 *  app's `Provider` declares twenty-two. Casting one to the other put
 *  `undefined` behind ten of them, and `money(undefined)` is **`SARNaN`** — so
 *  every provider card and every provider page read "Min. SARNaN" against the
 *  real API, on the app's main browse surface.
 *
 *  Three are renames, and the rest divide into derivable and absent:
 *
 *  · `bio` is `about`. Left unmapped it rendered as an empty paragraph.
 *  · `site.address` is a plain string where `site.street` is `LocalizedText`,
 *    and there is no `site.landmark` at all — so the site line rendered as a
 *    leading "· Al Hamra" with nothing before the separator.
 *  · `categoryIds[]` is `categoryId`. A partner can hold several server-side;
 *    this app models one, and takes the first. That is lossy and known — a
 *    partner listed under two categories shows under one here.
 *  · `initials`, `slot` and `photo` are **presentation**, derived here exactly
 *    as the seed derives them, so the two modes draw the same tile.
 *  · `minOrder`, `deliveryFee`, `etaMinutes`, `priceLevel`, `kind`, `openFrom`,
 *    `openTo` and `promo` are **absent from the wire** and are not invented.
 *    The first two are the interesting ones: they are *category* terms, not a
 *    partner's, which is the same conclusion `termsOf` reached on 13 Aug. They
 *    stay optional on the type and the screens stop rendering them; see
 *    `ProviderCard`. */
type WireProvider = Wire.Provider

/** The hue of the initials tile. The seed writes one per partner by hand to
 *  spread a category's providers across the ramp; there is no such field on the
 *  wire, so it is derived from the id — stable for a given partner, which is
 *  the property that matters, and spread well enough by the id's own entropy. */
function slotOf(id: string): Provider['slot'] {
  let hash = 0
  for (const ch of id) hash = (hash * 31 + ch.charCodeAt(0)) % 9973
  return ((hash % 9) + 1) as Provider['slot']
}

function fromWireProvider(wire: WireProvider): Provider {
  return {
    id: wire.id,
    slug: wire.slug,
    name: wire.name,
    categoryId: wire.categoryIds?.[0] ?? '',
    venues: wire.venues ?? ['we-come'],
    /* The same derivation approval uses, imported rather than repeated — it
       already handles a name with no Latin letters, which an Arabic-only
       partner name is. */
    initials: initialsOf(wire.name, wire.slug),
    /* The manifest key, not the wire's path: the service sends
       `/img/providers/<slug>.jpg`, a file this app does not serve, and `Photo`
       looks its slug up in the build manifest.
       **Today this resolves for none of them.** The manifest is built from the
       *seed's* twenty-two slugs (`pr-crisp-collar`, `pr-white-oud`, …) and the
       API seeds twelve entirely different ones (`olaya-laundry`, `cool-riyadh`,
       …) — the two sets do not intersect. `Photo` renders nothing for a key it
       does not hold, so cards and pages lose their cover art rather than
       showing a broken image, which is the right failure but is still a gap.
       Closing it means one set of slugs, or an asset host for the wire's paths;
       either is a decision about the image pipeline rather than about
       transport, so it is a route-matrix row. Deriving the key is still what
       this line should do — it is correct the moment the sets agree. */
    photo: `pr-${wire.slug}`,
    slot: slotOf(wire.id),
    rating: wire.rating ?? 0,
    reviewCount: wire.reviewCount ?? 0,
    etaMinutes: null,
    /* Filtered, not cast. The wire's `badges` is `string[]` — an eighth badge
       is one Go constant away, and this build would render its id. */
    badges: knownBadges(wire.badges),
    areas: wire.areas ?? [],
    about: wire.bio ?? { en: '', ar: '' },
    promo: null,
    ...(wire.site
      ? {
          site: {
            area: wire.site.area,
            street: bothScripts(wire.site.address),
            /* No landmark on the wire. Empty rather than repeating the street,
               which would render the same words twice under each other. */
            landmark: { en: '', ar: '' },
            lat: wire.site.lat,
            lng: wire.site.lng,
          },
        }
      : {}),
  }
}

/** A service area, as the API sends them. `key` is our `id`; the rest crosses
 *  unchanged now that `Area` has somewhere to put it.
 *
 *  The client's `getAreas` is typed `string[]` and the route answers objects,
 *  so `areas.data` was fifteen `{key,name,order}` records where the district
 *  picker expected fifteen strings — and `districtsByCity` filters its own
 *  table by `has(id)`, which no object ever satisfies. The result was an
 *  **empty district dropdown**, which is a form that cannot be submitted: no
 *  address, therefore no order.
 *
 *  Only `key` crosses. `name` is the same district in both scripts and `order`
 *  is the server's preferred sort — both worth having, and both a contract
 *  change rather than a cast, so they are a route-matrix row. What matters here
 *  is that the ids the server owns reach the screen at all. */
type WireArea = Wire.Area

/** A promo as the API advertises it.
 *
 *  This one is not a rename away from `Promo` and the difference is the kind
 *  that renders a wrong number rather than a missing one: **`value` is basis
 *  points** on a percent code, where this app's `Promo.value` is whole percent.
 *  A 15 % code is `1500` on the wire, and pasted across unconverted it reads as
 *  1500 % off. The original units are in `internal/domain/promo.go`; `toPromo`
 *  below is the only place that converts, and a test states the server's
 *  numbers so the pair cannot drift silently. */

/** `PublicPromo`, not `Promo`: the public list is the redeemable subset and
 *  carries no budget, no allow-list and no redemption count. */
type WirePromo = Wire.PublicPromo

/** A review as the API stores it.
 *
 *  Three of the five fields this app renders are spelled differently, and the
 *  two text ones are a different *type*: the service keeps `authorName` and
 *  `body` as plain strings where this app models both as `LocalizedText`. A
 *  reviewer signs their name in their own script and the API stores exactly
 *  what they typed, so there is no second translation to carry — the string is
 *  put in both slots rather than invented in one.
 *
 *  `providerName` and `categoryId` arrive only on the featured rail, where the
 *  service joins them on (`hexclean-api@4ee0dac`); a review read under a
 *  provider already has both from the screen around it. */
type WireReview = Wire.Review

const bothScripts = (text: string): LocalizedText => ({ en: text, ar: text })

function fromWireReview(wire: WireReview): Review {
  return {
    id: wire.id,
    providerId: wire.providerId,
    author: bothScripts(wire.authorName),
    /* `stars` on the wire, `rating` here — and it is the number the card draws
       its filled stars from, so an undefined renders as none of them. */
    rating: wire.stars,
    body: bothScripts(wire.body ?? ''),
    createdAt: wire.createdAt,
  }
}

/** The featured rail's review, which is a **different type on the wire** —
 *  `domain.FeaturedReview`, a `Review` plus the two fields the rail needs to
 *  caption a card it is showing out of context: whose partner it is, and under
 *  which category.
 *
 *  This read `WireReview` and reached for `providerName` and `categoryId` on
 *  it, which `domain.Review` has never carried. Both were declared optional, so
 *  both fell to their `??` defaults every time: the rail captioned every card
 *  with an empty name and filed it under an empty category. The generated
 *  model is what separated them — the fields do not exist on `Review`, and
 *  reading them stopped compiling. */
function fromWireFeatured(wire: Wire.FeaturedReview): FeaturedReview {
  return {
    ...fromWireReview(wire),
    providerName: wire.providerName,
    categoryId: wire.categoryId,
  }
}

/** A chosen window, as a booking reference.
 *
 *  `TimeSlot` is what the picker renders — it carries availability and a
 *  surcharge, neither of which means anything once the window is chosen. The
 *  server wants the four fields that identify it, and `kind` is not on the
 *  client's type at all: the picker knows which list it was filling. */
const toSlotRef = (slot: TimeSlot, kind: 'pickup' | 'delivery') => ({
  date: slot.date,
  from: slot.from,
  to: slot.to,
  kind,
})

type WireSlotRef = Wire.SlotRef

/** The reverse, for a window read back off a placed order.
 *
 *  `available` and `surcharge` are the picker's two fields and a booked window
 *  has neither: it is not a window you can still choose, and its uplift is
 *  already inside `totals.surcharge`. Filling them with `true` and `0` rather
 *  than widening `Order` to a third slot type keeps one shape for a window; the
 *  screens that render a placed order read only `date`, `from` and `to`. */
const fromSlotRef = (slot: WireSlotRef): TimeSlot => ({
  date: slot.date,
  from: slot.from,
  to: slot.to,
  available: true,
  surcharge: 0,
})

/** A priced basket, as `domain.Quote` sends it.
 *
 *  `totals` genuinely matches `OrderTotals` field for field — same names, same
 *  minor units, same meaning of `total` since `458003a` — so this is close to
 *  an identity. It is written out anyway, because the one time this app assumed
 *  that about an order it shipped a screen that crashed: a cast compiles
 *  against any JSON and a mapping does not.
 *
 *  The one field that is not always there is `promoError`. It is `omitempty`
 *  server-side, so a basket with no code and a code that was accepted both
 *  arrive without it, and only a refusal carries one. */
type WireQuote = Wire.Quote

const fromWireQuote = (wire: WireQuote): Quote => ({
  totals: wire.totals,
  minOrder: wire.minOrder,
  creditCeiling: wire.creditCeiling ?? 0,
  meetsMinimum: wire.meetsMinimum,
  promoApplied: wire.promoApplied,
  ...(wire.promoError ? { promoError: wire.promoError } : {}),
})

/** The partner application, which the two sides spell differently.
 *
 *  The path was realigned in Phase 1 and the body never was: this posted the
 *  form's own shape — `fullName`, `businessName` — at a server that wants
 *  `name` and `companyName`, so Join has never worked in `http` mode. Not a
 *  degradation either: `name` is required, so the request was refused outright.
 *
 *  `hexclean-partner` has carried the working version of this since Phase 3.
 *  It is duplicated here rather than shared because the two apps have no common
 *  package and inventing one for two functions is the larger change — but they
 *  are the same mapping, and they have to move together.
 *
 *  `venues` is required by the server and is not on this form: what a partner
 *  can offer follows from whether they declared premises, and asking twice
 *  would be a question with one right answer. */
type WireApplication = Wire.PartnerApplication

/** Ours to theirs, for submit. */
function toWireApplication(input: ApplicationInput): Partial<WireApplication> {
  const hasSite = Boolean(input.siteAddress?.trim())
  return {
    kind: input.kind === 'company' ? 'company' : 'individual',
    name: input.fullName.trim(),
    phone: input.phone,
    categoryIds: input.categoryIds,
    areas: input.areas,
    venues: hasSite ? ['we-come', 'you-come'] : ['we-come'],
    hasSite,
    ...(input.email?.trim() ? { email: input.email.trim() } : {}),
    ...(input.businessName?.trim() ? { companyName: input.businessName.trim() } : {}),
    ...(input.crNumber?.trim() ? { crNumber: input.crNumber.trim() } : {}),
    ...(input.siteAddress?.trim() ? { siteAddress: input.siteAddress.trim() } : {}),
    ...(input.note?.trim() ? { note: input.note.trim() } : {}),
  }
}

/** Theirs to ours, for the response.
 *
 *  `individual` collapses two of our kinds — a freelancer and a mobile operator
 *  are one thing to the Go domain — so coming back we cannot tell them apart and
 *  `freelancer` is the answer: the weaker of the two claims, and the field is
 *  only ever displayed. */
function fromWireApplication(wire: WireApplication): PartnerApplication {
  return {
    id: wire.id,
    ref: wire.ref,
    kind: wire.kind === 'company' ? 'company' : 'freelancer',
    categoryIds: wire.categoryIds as PartnerApplication['categoryIds'],
    fullName: wire.name,
    phone: wire.phone,
    areas: wire.areas ?? [],
    status: APPLICATION_STATUS[wire.status] ?? 'received',
    createdAt: wire.createdAt,
    ...(wire.email ? { email: wire.email } : {}),
    ...(wire.companyName ? { businessName: wire.companyName } : {}),
    ...(wire.crNumber ? { crNumber: wire.crNumber } : {}),
    ...(wire.vatNumber ? { vatRegistered: true } : {}),
    ...(wire.siteAddress ? { siteAddress: wire.siteAddress } : {}),
    ...(wire.note ? { note: wire.note } : {}),
    ...(wire.providerId ? { partnerId: wire.providerId } : {}),
  }
}

/** Two of the statuses are spelled differently on each side — `submitted` is
 *  what we call `received`, `in-review` is what we call `reviewing`. An
 *  unmapped value falling through as itself indexes the screen's copy table to
 *  `undefined`, which renders as a reference number with no sentence under it:
 *  the applicant is told nothing, on the one screen that exists to tell them
 *  something. */
const APPLICATION_STATUS: Record<string, PartnerApplication['status']> = {
  submitted: 'received',
  received: 'received',
  'in-review': 'reviewing',
  reviewing: 'reviewing',
  'docs-requested': 'docs-requested',
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
}

/** A reserved window.
 *
 *  `domain.SlotHold` json-tags its id as `holdId` and hides everything else
 *  behind `json:"-"` — the user, the list and the window are the service's
 *  bookkeeping, and the customer's screen already knows which window it asked
 *  for. So this is two fields, and `expiresAt` is an RFC 3339 timestamp rather
 *  than one of this API's `YYYY-MM-DD` dates: it is a deadline ten minutes from
 *  now, not a day. */
type WireSlotHold = Wire.SlotHold

/** The rail the customer picked, as a settlement method the service knows.
 *
 *  `domain.PaymentMethod` is `card | cash | wallet` and this app offers `mada`
 *  and `apple-pay` as well — so the **default** payment option was a 400 with
 *  nothing placed, on every order, until this existed.
 *
 *  Folding rather than growing the enum, and the reason is that the server's
 *  three are not a list of rails: they are *settlement behaviours*, and six
 *  branches across `orders.go`, `partner.go` and `settlement.go` switch on them
 *  — authorise-and-capture for `card`, collect-at-the-door for `cash`. mada and
 *  Apple Pay settle exactly as a card does, so `card` is what they are to every
 *  one of those branches. Adding `mada` as a fourth value would not be additive;
 *  it would be a mada order that never authorises and never settles.
 *
 *  What is genuinely lost is **which rail was chosen**, which a Saudi product
 *  has commercial reasons to keep (mada interchange is not card interchange).
 *  That is a field the API does not have — new capability, so a route-matrix row
 *  rather than something invented here. `instrument` is not it: that is the
 *  provider's opaque token, not a rail label. */
const toWireMethod = (method: Order['payment']): string =>
  method === 'mada' || method === 'apple-pay' ? 'card' : method

/** An order as the API stores it.
 *
 *  The write side of this was fixed in `60b7c40` and the read side was not,
 *  which is why `POST /orders` succeeded and the screen that opened on its
 *  answer crashed. Three of these are renames, one is a *type* change, and two
 *  fields this app renders are not on the wire at all:
 *
 *  · `slot` is `delivery` here, and `pickupSlot` is `pickup` — the same pair
 *    `toSlotRef` already spells correctly on the way out.
 *  · `payment` is an **object** server-side (method, state, amount, captured,
 *    refunded) where this app's `Order.payment` is the method string alone.
 *    The rest of it belongs to the settlement screens, which do not exist here.
 *  · `rating` is `*int` with `omitempty`, so an unrated order simply has no
 *    field — which is the `null` this app spells explicitly.
 *  · `assignedAt` has **no server-side equivalent**. It is optional on `Order`
 *    and stays undefined, which is what the screens already handle for an
 *    unassigned order.
 *
 *  `listId`, `address` and `updatedAt` arrive and are not carried; nothing
 *  renders them.
 *
 *  **`track` arrives and is still not carried, and that one is a bug.** Three
 *  screens read `category?.track ?? 'visit'` — the ladder a customer watches
 *  their order climb, derived from the category rather than taken from the
 *  order that has it. A forecourt car wash is the case that shows: the server
 *  maps a `you-come` booking onto a `dropoff` track and the category says
 *  `visit`, so the screen narrates a journey nobody is making. Carrying it
 *  means a field on this app's `Order`, three call sites, and the mock
 *  producing one too, so it is its own change rather than a line in this one.
 *  It is Issues row 20, and the wire model is what made it reachable. */
type WireOrder = Wire.Order

function fromWireOrder(wire: WireOrder): Order {
  return {
    id: wire.id,
    /* The server's reference, not one derived from the id.
       This read `displayRef(wire.id)` — the id's unique half, uppercased —
       under a comment asserting the service had no such field. B5 gave it one,
       and the comment outlived the fact. What that cost is precise: a customer
       reading `HTBBF0G4WY01G7WA` off their screen to support, who are looking
       at `ref` in the panel and cannot find it. The two are not merely
       different lengths, they are different strings for the same order. */
    ref: wire.ref,
    providerId: wire.providerId,
    categoryId: wire.categoryId,
    venue: wire.venue,
    /* `key` is rebuilt with the cart's own rule so a line read back off an
       order matches one in the basket it came from. `listId` is the order's,
       which is the list every line on it was priced against. */
    lines: (wire.lines ?? []).map((l) => ({
      key: lineKey(l.itemId, l.optionIds ?? []),
      itemId: l.itemId,
      listId: wire.listId as ListId,
      qty: l.qty,
      optionIds: l.optionIds ?? [],
    })),
    addressId: wire.addressId,
    pickup: wire.pickupSlot ? fromSlotRef(wire.pickupSlot) : null,
    delivery: fromSlotRef(wire.slot),
    payment: (wire.payment?.method ?? 'card') as Order['payment'],
    promoCode: wire.promoCode ?? null,
    totals: wire.totals,
    status: wire.status,
    /* `recordedAt` is dropped deliberately: it is when the event reached the
       service, and a timeline built from it tells the wrong story about a
       partner who was in a basement. `at` is the true moment. */
    events: (wire.events ?? []).map((e) => ({ status: e.status, at: e.at })),
    createdAt: wire.createdAt,
    rating: wire.rating ?? null,
  }
}

/** An address as the API stores it.
 *
 *  Two fields are the same thing under different names — the API's `line` is
 *  this app's `street`, and its `notes` are these `directions`. The rest now
 *  match: `nickname`, `additionalNo`, `floor` and `apartment` were added to the
 *  service on 13 Aug 2026 rather than folded into free text, because a rider
 *  reads a flat number on a phone in the dark and "flat 12" inside a sentence
 *  about a beige gate is a phone call.
 *
 *  Renaming `line` server-side instead would have been the tidier symmetry and
 *  is not available: `Address` is serialised into surfaces beyond this app, and
 *  breaking a field is a new API version. */
type WireAddress = Wire.Address

function fromWireAddress(wire: WireAddress): Address {
  return {
    id: wire.id,
    /* The API keeps this a free string deliberately — the three-way choice is
       this app's presentation, not the service's model. */
    label: wire.label as Address['label'],
    nickname: wire.nickname ?? '',
    area: wire.area,
    street: wire.line,
    building: wire.building ?? '',
    additionalNo: wire.additionalNo,
    floor: wire.floor,
    apartment: wire.apartment,
    directions: wire.notes,
    lat: wire.lat,
    lng: wire.lng,
    isDefault: wire.isDefault,
  }
}

/** The writable half, with the id left out.
 *
 *  Spelled field by field rather than spread-and-delete, because the handler
 *  decodes with `DisallowUnknownFields`: one key the server has no field for is
 *  a 400 for the whole request, not an ignored key. Sending the id in the body
 *  of a PATCH that already carries it in the path is exactly how this failed
 *  before — every address save, create and edit alike. */
function toWireAddress(input: Omit<Address, 'id'> & { id?: string }) {
  return {
    label: input.label,
    nickname: input.nickname,
    area: input.area,
    line: input.street,
    building: input.building,
    additionalNo: input.additionalNo ?? '',
    floor: input.floor ?? '',
    apartment: input.apartment ?? '',
    notes: input.directions ?? '',
    lat: input.lat,
    lng: input.lng,
    isDefault: input.isDefault,
  }
}

function toPromo(wire: WirePromo): Promo {
  return {
    code: wire.code,
    /* `fixed` server-side, `flat` here. Same thing, two spellings, and the
       client's third kind — free-delivery — is not one the API can send. */
    kind: wire.kind === 'fixed' ? 'flat' : 'percent',
    value: wire.kind === 'percent' ? wire.value / 100 : wire.value,
    minSpend: wire.minSubtotal,
    /* Zero is "no ceiling" on the wire and `null` is "no ceiling" here. Passed
       through as 0 it would read as a cap of nothing and show every percent
       discount as zero off. */
    cap: wire.maxDiscount > 0 ? wire.maxDiscount : null,
    description: wire.description,
    /* A code can be scoped to several categories server-side and this app's
       model holds one. Narrowing to a single id would hide a two-category code
       from one of the two, so anything other than exactly one reads as
       unscoped — it over-advertises rather than under-advertises, which is the
       side to err on for a code the customer can see the terms of. A promo
       rail that needs the real list wants `categoryIds` on `Promo`, and that
       is a contract change, not a cast. */
    categoryId: wire.categoryIds?.length === 1 ? (wire.categoryIds[0] ?? null) : null,
  }
}

/** The wire shape of a sign-in answer. Three outcomes share one route.
 *
 *  This is the one place in this file that maps rather than casts, because the
 *  server's session and `AuthSession` genuinely disagree: it sends `accessToken`
 *  and a `expiresIn` duration, this app stores `token` and an `expiresAt`
 *  instant. A mock cannot catch that — it answers in the client's own shape —
 *  so it surfaced only against the real API, as a 200 that signed nobody in. */
type WireSignIn = Wire.SignInResult

/** Posts a verified token and reads back whichever answer the API gave.
 *
 *  `phone` is present only on the leg that answers a `linkRequired`, and it is
 *  a plain number rather than a second token because there is nothing left to
 *  prove it with — the SMS challenge is gone. What this does not do is decide
 *  anything: which sign-ins need a number is the server's rule, and re-deriving
 *  it here would be two implementations to disagree the first time one
 *  changes. */
async function exchange(
  body: { idToken: string; phone?: string },
  pending: string,
  signal?: AbortSignal,
): Promise<SignInResult> {
  const result = await http.post<WireSignIn>(routes.signIn(), { ...PUBLIC, signal, body })

  if (result.linkRequired) {
    return { linkRequired: { email: result.linkRequired.email ?? '', pending } }
  }
  if (!result.accessToken || !result.user) return {}
  return {
    session: {
      user: result.user,
      token: result.accessToken,
      /* The server sends a lifetime, the session store wants a deadline.
         Computed from arrival rather than from any clock the server sent, so a
         device with a wrong clock is still signed out at roughly the right
         time rather than immediately or never. */
      expiresAt: new Date(Date.now() + (result.expiresIn ?? 900) * 1000).toISOString(),
    },
  }
}

export const httpApi = {
  /* ── catalog ────────────────────────────────────────────────────────────── */

  getCategories: (signal?: AbortSignal) =>
    http.get<ServiceCategory[]>(routes.categories(), { ...PUBLIC, signal }),

  getAreas: (signal?: AbortSignal) =>
    http
      .get<WireArea[]>(routes.areas(), { ...PUBLIC, signal })
      .then((areas) =>
        areas.map(
          (a): Area => ({ id: a.key, name: a.name, cityId: a.cityId, order: a.order }),
        ),
      ),

  /* No rename here: the server's city is already `{id,name,order}`. It gets a
     route of its own rather than being derived from the areas, because the
     "we serve …" line has to name a market that may have no districts on file
     yet, and a heading order taken from whichever district mentions a city
     first is not an order anybody chose. */
  getCities: (signal?: AbortSignal) =>
    http.get<City[]>(routes.cities(), { ...PUBLIC, signal }),

  getProviders: (filters: ProviderFilters = {}, signal?: AbortSignal) =>
    http.get<Page<WireProvider>>(routes.providers(), {
      ...PUBLIC,
      signal,
      /* Spelled out rather than spread from `filters`, so a field added to the
         type is a compile step here and a considered query parameter, not an
         accidental one leaking into the URL under its TypeScript name. */
      query: {
        category: filters.categoryId,
        venue: filters.venue,
        area: filters.area,
        q: filters.q,
        sort: filters.sort,
        minRating: filters.minRating,
        /* Repeatable keys would be the REST-idiomatic encoding, but the query
           builder takes a flat record and badges is the only array here.
           Comma-separated keeps one shape for every parameter. */
        badges: filters.badges?.length ? filters.badges.join(',') : undefined,
      },
    }).then((page) => items(page).map(fromWireProvider)),

  getProvider: (key: string, signal?: AbortSignal) =>
    http.get<WireProvider>(routes.provider(key), { ...PUBLIC, signal }).then(fromWireProvider),

  getMenu: (listId: ListId, signal?: AbortSignal) =>
    http.get<Menu>(routes.menu(listId), { ...PUBLIC, signal }),

  getReviews: (providerId: string, signal?: AbortSignal) =>
    http
      .get<Page<WireReview>>(routes.providerReviews(providerId), { ...PUBLIC, signal })
      .then((page) => items(page).map(fromWireReview)),

  getFeaturedReviews: (limit = 10, signal?: AbortSignal) =>
    http
      .get<Wire.FeaturedReview[]>(routes.featuredReviews(), { ...PUBLIC, signal, query: { limit } })
      .then((reviews) => reviews.map(fromWireFeatured)),

  search: (query: string, locale: 'en' | 'ar', signal?: AbortSignal) =>
    http.get<SearchHit[]>(routes.search(), { ...PUBLIC, signal, query: { q: query, locale } }),

  /* The API groups windows under the day they fall on; this app renders one
     flat list and reads the date off each window. Flattening is transport —
     every field the picker needs is already on the window, and `closed` days
     simply contribute nothing. */
  getSlots: (listId: ListId, days = 7, signal?: AbortSignal) =>
    http
      .get<WireSlotDay[]>(routes.slots(listId), { ...PUBLIC, signal, query: { days } })
      .then((wireDays) => wireDays.flatMap((day) => day.slots ?? [])),

  getPromos: (signal?: AbortSignal) =>
    http.get<WirePromo[]>(routes.promos(), { ...PUBLIC, signal }).then((promos) => promos.map(toPromo)),

  /* Sent without a bearer token only when there is none: the quote is
     per-customer once a session exists — the wallet balance and the promo
     redemption history are both properties of who is asking — so this is a
     read that is deliberately *not* marked PUBLIC. */
  getQuote: (input: QuoteInput, signal?: AbortSignal) =>
    http.post<Quote>(routes.quote(), {
      signal,
      body: {
        listId: input.listId,
        venue: input.venue,
        lines: input.lines.map((l) => ({
          itemId: l.itemId,
          qty: l.qty,
          optionIds: l.optionIds,
        })),
        /* Omitted rather than null when no window is chosen: the field is a
           pointer server-side, and a basket priced before the customer has
           picked a time simply has no surcharge to add yet. */
        ...(input.slot ? { slot: toSlotRef(input.slot, 'delivery') } : {}),
        promoCode: input.promoCode ?? '',
        useWallet: input.useWallet,
        providerId: input.providerId ?? '',
      },
    }).then(fromWireQuote),

  /* `to` is not sent. `WindowEnd` derives it from `from` server-side, and it is
     not a field on `holdRequest` — the decoder rejects unknown fields, so
     sending the end of the window this app already knows would fail the whole
     request. */
  holdSlot: (input: SlotHoldInput, signal?: AbortSignal) =>
    http
      .post<WireSlotHold>(routes.slotHold(), {
        signal,
        body: {
          listId: input.listId,
          date: input.date,
          from: input.from,
          kind: input.kind,
        },
      })
      .then((wire): SlotHold => ({ holdId: wire.holdId, expiresAt: wire.expiresAt })),

  releaseSlot: (holdId: string, signal?: AbortSignal) =>
    http.delete<void>(routes.slotHoldById(holdId), { signal }),

  /* ── auth ───────────────────────────────────────────────────────────────── */

  /* The three roads in, and they are one function shape on purpose: each mints
     a token that names an address and no number, and each may be carrying the
     number the customer has just typed.

     `pending` on the Google and password roads is the token *this client
     already holds* from the leg that came back `linkRequired`. Reused rather
     than re-minted, because re-minting means another account chooser or another
     password — and the number the customer is submitting was asked for about
     that particular sign-in. */
  signInWithGoogle: async (input, signal) => {
    const idToken = input?.pending ?? (await identity.withGoogle())
    return exchange(
      { idToken, ...(input?.phone ? { phone: input.phone } : {}) },
      idToken,
      signal,
    )
  },

  signInWithPassword: async (input, signal) => {
    const idToken = await identity.withPassword(input.email, input.password)
    return exchange({ idToken, ...(input.phone ? { phone: input.phone } : {}) }, idToken, signal)
  },

  /* Registration sends the number in the same call, because the form collected
     it. A new account has never named one, so the API would answer
     `linkRequired` immediately — and asking somebody for a number they typed
     forty seconds ago is the sort of step that reads as a broken form. */
  signUpWithPassword: async (input, signal) => {
    const idToken = await identity.createAccount(input.email, input.password)
    return exchange({ idToken, phone: input.phone }, idToken, signal)
  },

  resendVerification: (email: string, password: string) =>
    identity.resendVerification(email, password),

  /* ── password recovery ──────────────────────────────────────────────────── */

  /* Not one of them touches `http`. The provider holds the password, mails the
     link and spends the code; HexClean has nothing to add to any of that and no
     way to verify it if it tried. They are on the API surface because the
     screens should import from one place — not because there is a route. */

  requestPasswordReset: (email: string) => identity.requestPasswordReset(email),

  checkPasswordReset: async (code: string) => ({
    email: await identity.checkPasswordReset(code),
  }),

  completePasswordReset: (input: { code: string; password: string }) =>
    identity.completePasswordReset(input.code, input.password),

  confirmEmail: (code: string) => identity.applyCode(code),

  getMe: (signal?: AbortSignal) => http.get<User>(routes.me(), { signal }),

  updateProfile: (patch: Partial<User>, signal?: AbortSignal) =>
    http.patch<User>(routes.me(), { signal, body: patch }),

  /* `auth: false` is not a mistake. This request's credential is the refresh
     cookie, and the access token it is trying to replace is by definition the
     expired one — sending it would invite the client's own 401 handler to
     recurse into the call that is meant to break the loop. */
  refreshSession: async (signal?: AbortSignal) => {
    try {
      const result = await http.post<WireSignIn>(routes.refresh(), { auth: false, signal })
      if (!result.accessToken || !result.user) return null
      return {
        user: result.user,
        token: result.accessToken,
        expiresAt: new Date(Date.now() + (result.expiresIn ?? 900) * 1000).toISOString(),
      }
    } catch (err) {
      /* A refusal here means the cookie is gone, expired or already spent —
         the session has ended, which is not an error to report. Anything else
         is a real failure and is left to propagate. */
      if (err instanceof HexApiError && err.code === 'unauthorized') return null
      throw err
    }
  },

  /* Two sessions end here, not one. Revoking the refresh token is what stops
     HexClean itself recognising this device; dropping the provider's own
     session is what stops the *next* person on a shared handset finding the
     previous one's Google account pre-selected, or a "create an account" screen
     that refuses because somebody is still signed in behind it.

     The provider half is not awaited into the failure path: sign-out is a thing
     the customer asked for and has to happen on this device whatever the
     network says. */
  signOut: async (signal?: AbortSignal) => {
    void identity.forget()
    await http.post<void>(routes.signOut(), { signal })
  },

  registerDevice: (token: string, signal?: AbortSignal) =>
    http.post<void>(routes.devices(), { signal, body: { token, platform: 'web' } }),

  unregisterDevice: (token: string, signal?: AbortSignal) =>
    http.delete<void>(routes.devices(), { signal, body: { token } }),

  /* ── addresses ──────────────────────────────────────────────────────────── */

  getAddresses: (signal?: AbortSignal) =>
    http.get<WireAddress[]>(routes.addresses(), { signal }).then((list) => list.map(fromWireAddress)),

  /* One method, two verbs: an address with an id is an edit and without one is
     a create. Splitting it would push the decision into every caller, and the
     form that produces it genuinely does not care which it is doing.

     PATCH rather than PUT on the edit. The form sends the whole address, so
     either would work today, but the route is a PATCH and a client that sends
     PUT gets a 405 — and the difference stops being cosmetic the moment a
     field the form does not render is added to the record. */
  saveAddress: (input: Omit<Address, 'id'> & { id?: string }, signal?: AbortSignal) =>
    (input.id === undefined
      ? http.post<WireAddress>(routes.addresses(), { signal, body: toWireAddress(input) })
      : http.patch<WireAddress>(routes.address(input.id), {
          signal,
          body: toWireAddress(input),
        })
    ).then(fromWireAddress),

  deleteAddress: (id: string, signal?: AbortSignal) =>
    http.delete<void>(routes.address(id), { signal }),

  /* ── favourites ─────────────────────────────────────────────────────────── */

  getFavourites: (signal?: AbortSignal) =>
    http.get<WireProvider[]>(routes.favourites(), { signal }).then((list) => list.map(fromWireProvider)),

  /* One path, two verbs, and the caller names the state it wants. This is the
     un-favourite that did not exist: the API has served DELETE all along and
     this client had no caller for it, so Saved was a one-way list — a shop
     could be hearted and never un-hearted against a real backend. */
  setFavourite: (providerId: string, saved: boolean, signal?: AbortSignal) =>
    saved
      ? http.put<void>(routes.favourite(providerId), { signal })
      : http.delete<void>(routes.favourite(providerId), { signal }),

  /* ── orders ─────────────────────────────────────────────────────────────── */

  /* The one request in this file whose body is a genuine projection rather
     than a cast, and it has to be: the service names six of these fields
     differently and has no field at all for three more the client carries.
     With `DisallowUnknownFields` on the decoder, posting this app's own shape
     was not a partially-applied order — it was a 400 with nothing placed.

     Spelled out rather than spread, so a field added to `PlaceOrderInput` is a
     compile step here and a considered part of the body. */
  placeOrder: (input: PlaceOrderInput, signal?: AbortSignal) =>
    http.post<WireOrder>(routes.orders(), {
      signal,
      /* Header, not body. `IdempotencyKey` is `json:"-"` server-side and the
         middleware keys its stored response off the header — a body field that
         disagreed would hand the payment provider a different key on the
         replay than on the original. */
      idempotencyKey: input.idempotencyKey,
      body: {
        listId: input.listId,
        venue: input.venue,
        /* `QuoteLine`, not `CartLine`. The `key`, `listId` and `notes` this app
           carries per line are all unknown fields, and any one of them fails
           the whole request. */
        lines: input.lines.map((l) => ({
          itemId: l.itemId,
          qty: l.qty,
          optionIds: l.optionIds,
        })),
        /* Omitted when absent rather than sent as null: these are
           `string,omitempty` server-side, and Go decodes a JSON null into the
           zero value anyway — but an explicit empty string is what the field
           means here, and it round-trips identically. */
        addressId: input.addressId ?? '',
        providerId: input.providerId ?? '',
        promoCode: input.promoCode ?? '',
        /* The reservation being spent. Empty where checkout never got one, and
           `ConsumeHold` treats that as "no hold to consume" rather than as an
           error — the window is re-checked either way. */
        holdId: input.holdId ?? '',
        slot: toSlotRef(input.delivery, 'delivery'),
        ...(input.pickup ? { pickupSlot: toSlotRef(input.pickup, 'pickup') } : {}),
        /* Derived rather than passed: the basket's toggle is already expressed
           in the totals it produced, and sending both invites them to
           disagree. */
        useWallet: input.totals.walletApplied > 0,
        paymentMethod: toWireMethod(input.payment),
        /* The number, not the object — and it is `total`, which since
           `458003a` means what the server means by it. Checked and not
           trusted: a disagreement comes back as a conflict carrying the
           correct totals, so checkout can re-render rather than charge a
           figure the customer never saw. */
        expectedTotal: input.totals.total,
      },
    }).then(fromWireOrder),

  /* Paginated, like every list that can grow. Casting the envelope to an array
     is not a type error anywhere — it is an object with no `.map`, so Orders
     renders empty or throws on the first customer who has ever ordered. */
  getOrders: (signal?: AbortSignal) =>
    http
      .get<Page<WireOrder>>(routes.orders(), { signal })
      .then((page) => items(page).map(fromWireOrder)),

  getOrder: (id: string, signal?: AbortSignal) =>
    http.get<WireOrder>(routes.order(id), { signal }).then(fromWireOrder),

  /* `advanceOrder` is deliberately absent, and permanently so — the same
     mechanism the partner app uses for `requestServiceChange`. The tracking
     screen's simulated courier is a mock affordance; a customer who could
     advance their own order could mark their own delivery complete, which is
     what decides when money settles and when the cancellation window shuts.
     Against a real service the partner app reports progress and this app
     polls. There is no route to add later. */

  /* The empty body is not optional. Every handler that reads a request decodes
     with `DisallowUnknownFields`, and the decoder is handed an empty stream
     when there is no body at all — which fails before the handler runs and
     answers 400 "that request could not be read". A cancel with no reason has
     to say so in JSON.

     The reason itself is genuinely optional to the service, which stores it and
     branches on nothing. Sending the empty string is therefore honest rather
     than a placeholder: this screen has no field to collect one. If it ever
     grows one, the value goes here and the contract grows a parameter. */
  cancelOrder: (id: string, signal?: AbortSignal) =>
    http
      .post<WireOrder>(routes.cancelOrder(id), { signal, body: { reason: '' } })
      .then(fromWireOrder),

  /* POST, not PUT. A rating is written once and the route is idempotency-aware
     rather than idempotent — re-rating is a thing the service decides about,
     not something the verb quietly permits.

     It answers **201 with the Review**, not with the order. The mock returned
     the order because writing a number onto it was all the mock did; the
     service records a review against the assigned partner and recomputes their
     score, which is what moves them in the recommended sort and the assignment
     queue. The caller re-reads the order rather than being handed one. */
  rateOrder: (id: string, stars: number, signal?: AbortSignal) =>
    http
      .post<WireReview>(routes.rateOrder(id), { signal, body: { stars } })
      .then(fromWireReview),

  /* ── supply: applications ───────────────────────────────────────────────── */

  submitApplication: async (input: ApplicationInput, signal?: AbortSignal) =>
    fromWireApplication(
      await http.post<WireApplication>(routes.applications(), {
        signal,
        body: toWireApplication(input),
      }),
    ),

  /* The nineteen operator methods that used to close this file — the
     application pipeline, document review, activation, partner staff and
     payout details — were removed in Phase 1. No screen in this app called
     any of them; they duplicated `hexclean-admin` inside a customer bundle.
     `satisfies HexApi` is what proves the contract and this adapter agree,
     so if one comes back without the other, this file stops compiling. */
} satisfies HexApi
